import { DataTable, Dialog, FormLabel, Heading, Loader, Select } from "@/components";
import { Button, Input, Label } from "@/components/ui";
import { Plus, Search } from "lucide-react";
import { DESCRIPTION_MAX_LENGTH, TITLE_MAX_LENGTH } from "./consts";
import useFinancialStatementVersions from "./useFinancialStatementVersions";

/**
 * Master Data > Financial & Billing > Financial Statement Versions (admin): the community's report structures
 * (Category > Subcategory > GL accounts). Drafts are edited in the builder; published versions are read-only
 * and changed through a New Version.
 */
const FinancialStatementVersions = () => {
  const {
    community,
    communityId,
    communityList,
    isCommunityLoading,
    isLoading,
    search,
    setSearch,
    createOpen,
    setCreateOpen,
    title,
    setTitle,
    description,
    setDescription,
    createError,
    toDelete,
    setToDelete,
    rows,
    columns,
    onCommunityChange,
    openCreate,
    onCreate,
    onDelete,
    openBuilder,
    isCreating,
    isDeleting,
  } = useFinancialStatementVersions();

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-3 lg:items-end lg:justify-between mb-6">
        <div>
          <Heading title="Financial Statement Versions" />
          <p className="text-sm text-gray-500">Report structures for a community: categories, subcategories and GL accounts in the order reports show them.</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="w-full sm:w-72">
            <FormLabel>Select Community</FormLabel>
            <Select
              options={communityList?.data?.map((c: any) => ({ value: c?.communityId?.toString(), label: c?.name }))}
              value={communityId ?? ""}
              onChange={onCommunityChange}
              className="h-[36px] w-full"
              disabled={isCommunityLoading}
            />
          </div>
          <Input type="text" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64 bg-white h-[40px]" startIcon={<Search className="w-4 h-4" />} />
          <Button onClick={openCreate} disabled={!communityId}><Plus className="w-4 h-4" /> New Financial Version</Button>
        </div>
      </div>

      {!communityId ? (
        <p className="text-sm text-gray-500">Select a community to see its financial statement versions.</p>
      ) : isLoading ? (
        <Loader />
      ) : (
        <DataTable data={rows} columns={columns} onRowClick={(row: any) => openBuilder(row.original.financialTemplateId)} />
      )}

      <Dialog open={createOpen} setOpen={setCreateOpen} hideFooter title="New Financial Version">
        <div className="flex flex-col gap-4">
          <p className="text-xs text-gray-500">Created as a draft in {community?.name}. You'll build the categories next.</p>
          <div className="flex flex-col gap-1">
            <Label htmlFor="fsv-title">Financial Version Name *</Label>
            <Input id="fsv-title" maxLength={TITLE_MAX_LENGTH} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. FY 2027 Management Reporting" />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="fsv-desc">Description</Label>
            <Input id="fsv-desc" maxLength={DESCRIPTION_MAX_LENGTH} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {createError && <p className="text-sm text-red-600">{createError}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={isCreating}>Cancel</Button>
            <Button onClick={onCreate} disabled={isCreating}>Create &amp; Build</Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={!!toDelete} setOpen={(o: boolean) => !o && setToDelete(null)} hideFooter title="Delete draft?">
        <div className="flex flex-col gap-4">
          <p className="text-sm">"{toDelete?.name}" and its structure will be deleted. This can't be undone.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setToDelete(null)} disabled={isDeleting}>Cancel</Button>
            <Button variant="destructive" disabled={isDeleting} onClick={onDelete}>Delete</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default FinancialStatementVersions;
